import { getConfig } from '../client/config';
import { getActiveSessionManager } from './session-manager';
import { ServerSessionManager } from './server-session-manager';
import { decodeBase64Url } from './core-platform';
import { normalizeEvmAddress } from './evm/evm-utils';
import { getFreshAuthToken } from './auth-token-freshness';
export async function getMessageStatement() {
    const config = await getConfig();
    return `Authenticating to Bounded App With ID ${config.appId}`;
}
async function createBearerToken(isServer) {
    // Both branches go through the freshness helper now. The web branch used to read
    // localStorage synchronously and send whatever was there, so an expired token was
    // handed to the server on every request and only recovered from AFTER a 401 —
    // and until the worker started returning 401 for a rejected bearer, never.
    //
    // A throw here is deliberate and must propagate: it means a session exists but
    // cannot be used. Swallowing it and sending the request anonymously would put
    // the caller back in policy as an anonymous user, which is the original bug.
    const idToken = await getFreshAuthToken(isServer);
    if (!idToken) {
        return null;
    }
    return `Bearer ${idToken}`;
}
export async function createAuthHeader(isServer) {
    const bearerToken = await createBearerToken(isServer);
    if (!bearerToken) {
        return null;
    }
    return {
        Authorization: bearerToken
    };
}
export async function getUserInfo(isServer) {
    const idToken = await getIdToken(isServer);
    if (!idToken) {
        return null;
    }
    // Decode the JWT to get user info (base64url-safe)
    const payload = JSON.parse(decodeBase64Url(idToken.split('.')[1]));
    return payload;
}
;
/**
 * Derive { id, address, email } from an idToken's claims.
 *
 * Mirrors the realtime-worker auth.ts identity resolution so the client-side
 * `user` object matches what the backend authenticates as:
 *   - id  = custom:userId only.
 *   - address = custom:walletAddress only (a REAL wallet). NEVER falls back to the
 *           identity: an opaque id is not a spendable onchain address. null for
 *           email-only sessions.
 *   - email = the token's `email` claim, lowercased to match the canonical form
 *           policy equality compares against. null when absent (wallet/SIWS).
 *
 * Returns all-null when the token can't be decoded (caller treats as no identity).
 */
export function deriveUserIdentityFromIdToken(idToken) {
    const empty = { id: null, address: null, email: null, evmAddress: null };
    if (!idToken)
        return empty;
    try {
        const parts = idToken.split('.');
        if (parts.length < 2)
            return empty;
        const payload = JSON.parse(decodeBase64Url(parts[1]));
        const walletClaim = payload['custom:walletAddress'];
        const address = (typeof walletClaim === 'string' && walletClaim.length > 0)
            ? walletClaim
            : null;
        const userIdClaim = payload['custom:userId'];
        let id = (typeof userIdClaim === 'string' && userIdClaim.length > 0)
            ? userIdClaim
            : null;
        const emailClaim = payload['email'];
        const email = (typeof emailClaim === 'string' && emailClaim.length > 0)
            ? emailClaim.toLowerCase()
            : null;
        // EVM wallet — carried on the `custom:evmWalletAddress` claim (an EVM login
        // OMITS custom:walletAddress). Normalized to canonical lowercase 0x-hex the
        // SAME way the realtime-worker does, so the client @user.evmAddress and the
        // server-authenticated one are byte-identical (fail-closed on malformed).
        const evmAddress = normalizeEvmAddress(payload['custom:evmWalletAddress']);
        // A SIWE identity uses its EVM address as custom:userId. Normalize legacy
        // mixed-case tokens at this boundary too so the principal and
        // @user.evmAddress cannot diverge solely by hex casing. Opaque human-login
        // ids remain byte-for-byte unchanged even when they have a linked wallet.
        if (evmAddress && id && normalizeEvmAddress(id) === evmAddress)
            id = evmAddress;
        return { id, address, email, evmAddress };
    }
    catch (_a) {
        return empty;
    }
}
export async function getIdToken(isServer) {
    if (isServer) {
        const sessionMgr = ServerSessionManager.instance;
        const session = await sessionMgr.getSession();
        return session === null || session === void 0 ? void 0 : session.idToken;
    }
    return getActiveSessionManager().getIdToken();
}
export async function getRefreshToken(isServer) {
    if (isServer) {
        const sessionMgr = ServerSessionManager.instance;
        const session = await sessionMgr.getSession();
        return session === null || session === void 0 ? void 0 : session.refreshToken;
    }
    return getActiveSessionManager().getRefreshToken();
}
/** The auth issuer base that minted the active client session, so a refresh POSTs
 *  to the right /session/refresh. Undefined on the server (keypair, no refresh). */
export function getSessionIssuer(isServer) {
    var _a;
    if (isServer)
        return undefined;
    return (_a = getActiveSessionManager().getIssuer()) !== null && _a !== void 0 ? _a : undefined;
}
/**
 * Read the active session's generation, minting one for a pre-generation session.
 *
 * The server manager is a single in-memory session per process, so it has no
 * cross-realm lineage problem and reports null.
 */
export async function getSessionGeneration(isServer = false) {
    var _a;
    if (isServer)
        return null;
    const session = await getActiveSessionManager().readSessionWithGeneration();
    return (_a = session === null || session === void 0 ? void 0 : session.sessionGeneration) !== null && _a !== void 0 ? _a : null;
}
/**
 * Commit refreshed tokens, bound to the generation they were minted for.
 *
 * Replaces the old void-returning `updateIdTokenAndAccessToken`. That version
 * silently dropped a write its cross-principal guard refused, so callers went on
 * to use a token that had NOT been stored — letting a request execute as the
 * previous user while storage and the UI already showed a new one. Callers must
 * branch on the outcome and use `session`, never the token they passed in.
 */
export async function commitRefreshedTokens(expected, idToken, accessToken, isServer = false, refreshToken) {
    if (isServer) {
        const sessionMgr = ServerSessionManager.instance;
        // Ensure we have a session to update; if missing, let lazy creation happen on next request.
        let session = null;
        try {
            session = await sessionMgr.getSession();
        }
        catch (_a) {
            return { outcome: "superseded", session: null };
        }
        if (!session) {
            return { outcome: "superseded", session: null };
        }
        const next = Object.assign(Object.assign(Object.assign({}, session), { idToken,
            accessToken }), (refreshToken ? { refreshToken } : {}));
        sessionMgr.setSession(next);
        return { outcome: "applied", session: next };
    }
    return await getActiveSessionManager().commitRefreshedTokens(expected, idToken, accessToken, refreshToken);
}
